import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { DeviceDetectorResult } from 'device-detector-js';
import { startWith, takeUntil } from 'rxjs/operators';
import { ActionShowModal, AndCondition, ArrayLiteralValue, BannerModel, CheckboxGroupModel, CheckBoxModel, CheckedCondition, CheckerServiceModel, ComponentModel, ConditionalValue, ConditionID, ConditionModel, EqConditionModel, FieldsetModel, FormModel, GtCondition, InArrayCondition, InfoModel, LabelModel, LiteralValueModel, LtCondition, ModalModel, ModalOpenerModel, ModelDetail, NotBlankCondition, NotConditionModel, NumberCondition, OptionModel, OrCondition, OTPServiceModel, PathValueModel, PatternConditionModel, ReviewModel, SelectModel, SelectTextValue, ServiceHandlerModel, ServiceStepModel, StepModel, TextInputModel, TruthyCondition } from '../../form-json.types';
import { State } from '../../store';
import { FormState, selectForm, updateFormStateAction } from '../../store/form';
import { StatusState, updateStatusStateAction } from '../../store/status';
import { StepsValidityState, updateStepValidityAction } from '../../store/step-validity';
import { ConditionService } from '../condition/condition.service';
import { DataService } from '../data/data.service';
import { DestroyService } from '../destroy/destroy.service';
import { FormCoreModule } from '../form-core.module';

@Injectable({
  providedIn: FormCoreModule
})
export class SetupService {

  constructor(private store: Store<State>, private des: DestroyService, private condition: ConditionService, private data: DataService) { }

  updateState(model: FormModel) {
    model.included.forEach(detail => this.updateModel(detail))
    this.data.attributes = model.data.attributes
    this.data.relationships = {
      step: model.data.relationships.steps.data
    }
    const presences: { formControl: FormControl, component: ComponentModel }[] = []
    for (let id in this.data.model['text-component']) {
      this.setupControl(this.data.model['text-component'][id], presences, model)
    }
    for (let id in this.data.model['select-component']) {
      this.setupControl(this.data.model['select-component'][id], presences, model)
    }
    for (let id in this.data.model['checkbox-group-component']) {
      this.setupControl(this.data.model['checkbox-group-component'][id], presences, model)
    }
    for (let id in this.data.model['path-value']) {
      if (!this.data.getControl(this.data.model['path-value'][id].attributes.path)) this.data.pathValue[this.data.model['path-value'][id].attributes.path] = undefined
    }
    if (presences.length) this.store.pipe(
      select(selectForm),
      takeUntil(this.des.destroy$)
    ).subscribe(() => presences.forEach(item => item.component.relationships.presences?.data?.every(value => this.condition.assign(value)) ? item.formControl.disabled && item.formControl.enable() : item.formControl.enabled && item.formControl.disable()))
  }

  setupDevice(device: DeviceDetectorResult) {
    this.data.device = device
  }

  setupQueryParams() {
    window.location.search.substring(1).split('&').forEach(query => {
      const [key, value] = query.split('=')
      this.data.queryParams[key] = value
    })
  }

  private setupControl(component: ComponentModel, presences: { formControl: FormControl, component: ComponentModel }[], model: FormModel) {
    const formControl = component.type === 'checkbox-group-component' ? new FormControl([]) : new FormControl('')
    const path = component.attributes.path
    const fieldsetID = component.relationships.fieldset.data.id
    const fieldset = this.data.model.fieldset[fieldsetID]
    const stepID = fieldset.relationships.step.data.id
    if (component.type === 'checkbox-group-component') {
      if (component.relationships.validations?.data) {
        const checkedCondition = component.relationships.validations.data.findIndex(condition => condition.type === 'checked-condition')
        const errorMessage = checkedCondition > -1 ? this.data.getModel<CheckedCondition>(component.relationships.validations.data[checkedCondition]).attributes['error-message'] : undefined
        if (checkedCondition > -1) {
          component.relationships.validations.data.splice(checkedCondition, 1)
          component.attributes.required = true
          if (!component.attributes['required-error-message']) component.attributes['required-error-message'] = errorMessage
        }
      }
      const newValue: any[] = []
      component.relationships.components.data.forEach(checkbox => {
        const { attributes: { value, checked } } = this.data.getModel<CheckBoxModel>(checkbox)
        if (checked) newValue.push(value)
      })
      if (newValue.length > 0) formControl.setValue(newValue)
    }
    if (!this.data.stepsFormGroup[stepID]) this.data.stepsFormGroup[stepID] = new FormGroup({})
    this.data.formControls[path] = formControl
    this.data.stepsFormGroup[stepID].addControl(component.attributes.path, formControl)
    const validations: ValidatorFn[] = !component.relationships.validations?.data?.length ? [] : component.relationships.validations.data.map(condition => this.convertToValidatorFn(condition))
    formControl.setValidators(this.compose(validations, { required: !!component.attributes.required, 'required-error-message': component.attributes['required-error-message'] }))
    formControl.updateValueAndValidity()
    if (component.attributes.disabled) {
      formControl.disable()
    } else if (component.relationships.presences?.data?.length) {
      presences.push({ formControl, component })
    }
    formControl.valueChanges.pipe(
      startWith(formControl.value),
      takeUntil(this.des.destroy$)
    ).subscribe(value => {
      const obj: FormState = {}
      obj[model.data.id] = {}
      obj[model.data.id][path] = value
      this.store.dispatch(updateFormStateAction(obj))
    })
    formControl.statusChanges.pipe(
      startWith(formControl.status),
      takeUntil(this.des.destroy$)
    ).subscribe(status => {
      const obj: StatusState = {}
      obj[path] = status
      this.store.dispatch(updateStatusStateAction(obj))
    })
    this.data.stepsFormGroup[stepID].statusChanges.pipe(
      startWith(this.data.stepsFormGroup[stepID].status),
      takeUntil(this.des.destroy$)
    ).subscribe(status => {
      const obj: StepsValidityState = {}
      obj[stepID] = status
      this.store.dispatch(updateStepValidityAction(obj))
    })
  }

  private updateModel<T extends ModelDetail>(detail: T) {
    const key = detail.type
    this.data.model[key][detail.id] = detail
  }

  private convertToValidatorFn(condition: ConditionID): ValidatorFn {
    const errorMessage = this.data.getModel<ConditionModel>(condition).attributes?.['error-message'] as string
    const error: ValidationErrors = {}
    error[errorMessage] = true
    return (abstract: AbstractControl): null | ValidationErrors => {
      return this.condition.assign(condition) ? null : error
    }
  }

  private compose(validations: ValidatorFn[], options?: { required: boolean, 'required-error-message'?: string }): ValidatorFn[] {
    let _validations: ValidatorFn[] = [...validations]
    if (options?.required) {
      _validations.unshift((control: AbstractControl) => {
        const result = Validators.required(control)
        if (result === null) return null
        const key = options['required-error-message'] as string
        const obj: ValidationErrors = {}
        obj[key] = true
        return obj
      })
    } else if (validations.length) {
      _validations = validations.map(fn => (control: AbstractControl) => Validators.required(control) === null ? fn(control) : null)
    }
    return _validations
  }
}

interface TypeObj<T> {
  [key: string]: T
}

export interface Model {
  step: TypeObj<StepModel>,
  fieldset: TypeObj<FieldsetModel>,
  'text-component': TypeObj<TextInputModel>,
  'field-label': TypeObj<LabelModel>,
  'path-value': TypeObj<PathValueModel>,
  'literal-value': TypeObj<LiteralValueModel>,
  'eq-condition': TypeObj<EqConditionModel>,
  'not-condition': TypeObj<NotConditionModel>,
  'pattern-condition': TypeObj<PatternConditionModel>,
  'array-literal-value': TypeObj<ArrayLiteralValue>,
  'in-array-condition': TypeObj<InArrayCondition>,
  'and-condition': TypeObj<AndCondition>,
  'conditional-value': TypeObj<ConditionalValue>,
  'truthy-condition': TypeObj<TruthyCondition>,
  'or-condition': TypeObj<OrCondition>,
  'not-blank-condition': TypeObj<NotBlankCondition>,
  'number-condition': TypeObj<NumberCondition>,
  'gt-condition': TypeObj<GtCondition>,
  'lt-condition': TypeObj<LtCondition>,
  'select-component': TypeObj<SelectModel>,
  'option-component': TypeObj<OptionModel>,
  'intro-component': TypeObj<BannerModel>,
  'modal-opener': TypeObj<ModalOpenerModel>,
  'modal-all-requirements-component': TypeObj<ModalModel>,
  'modal-direct-marketing-policy-component': TypeObj<ModalModel>,
  'info-component': TypeObj<InfoModel>,
  'checkbox-component': TypeObj<CheckBoxModel>,
  'checkbox-group-component': TypeObj<CheckboxGroupModel>,
  'checked-condition': TypeObj<CheckedCondition>,
  'service-step': TypeObj<ServiceStepModel>,
  'otp-service': TypeObj<OTPServiceModel>,
  'checker-service': TypeObj<CheckerServiceModel>,
  'service-handler': TypeObj<ServiceHandlerModel>,
  'action-show-modal': TypeObj<ActionShowModal>,
  'modal-service-status-component': TypeObj<ModalModel>,
  'review-component': TypeObj<ReviewModel>,
  'select-text-value': TypeObj<SelectTextValue>
}